import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import {
  AiImpactAssessmentTable,
  ManualImpactAssessmentTable,
  PlanManagementDialog,
  ProcessMappingSettingDialog,
} from "../components/AssessmentCenterPage";
import { PageSectionTabLabel } from "../components/shared/PageSectionTabLabel";
import { assessmentCenterPlansLikeButtonSx } from "../components/AssessmentCenterPage/assessmentCenterFilterBarSx";
import {
  pageSectionMainAreaSx,
  pageSectionTabStripSx,
  pageSectionTabSx,
  pageSectionTabsSx,
} from "../constants/pageSectionStyles";
import { useHasPrivilegeStatus } from "../hooks/useHasPrivilege";
import { useModuleDisplayName } from "../hooks/useModuleDisplayName";
import arrowLeftIcon from "../assets/ArrowLeft.svg";
import iconProcessMapping from "../assets/icon-process-mapping.svg";

type AssessmentTabKey = "aiImpact" | "manualImpact";

/** Prefer AI history when allowed; otherwise Manual; otherwise null. */
export function pickDefaultAssessmentCenterTab(
  canReadImpact: boolean,
  canReadManual: boolean,
): AssessmentTabKey | null {
  if (canReadImpact) return "aiImpact";
  if (canReadManual) return "manualImpact";
  return null;
}

type AssessmentCenterHistoryBodyProps = Readonly<{
  showAccessDenied: boolean;
  showAiTab: boolean;
  showManualTab: boolean;
  tab: AssessmentTabKey;
  activeTab: AssessmentTabKey | false;
  onTabChange: (next: AssessmentTabKey) => void;
  onOpenPlanManagement: () => void;
}>;

function AssessmentCenterHistoryBody({
  showAccessDenied,
  showAiTab,
  showManualTab,
  tab,
  activeTab,
  onTabChange,
  onOpenPlanManagement,
}: AssessmentCenterHistoryBodyProps) {
  if (showAccessDenied) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: 1,
          px: 3,
          py: 6,
        }}
      >
        <Typography sx={{ color: "#64748b", fontSize: "0.875rem" }}>
          You do not have permission to view Assessment Center history.
        </Typography>
      </Box>
    );
  }

  if (!showAiTab && !showManualTab) {
    return null;
  }

  return (
    <>
      <Box sx={pageSectionTabStripSx}>
        <Tabs
          value={activeTab}
          onChange={(_e, v: AssessmentTabKey) => onTabChange(v)}
          sx={pageSectionTabsSx}
        >
          {showAiTab ? (
            <Tab
              value="aiImpact"
              label={
                <PageSectionTabLabel line1="AI Impact" line2="Assessment" />
              }
              sx={{ ...pageSectionTabSx, width: 120, minWidth: 120 }}
            />
          ) : null}
          {showManualTab ? (
            <Tab
              value="manualImpact"
              label={
                <PageSectionTabLabel
                  line1="Manual Impact"
                  line2="Assessment"
                />
              }
              sx={{ ...pageSectionTabSx, width: 130, minWidth: 130 }}
            />
          ) : null}
        </Tabs>
      </Box>
      {showAiTab ? (
        <Box
          sx={{
            display: tab === "aiImpact" ? "flex" : "none",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}
        >
          {tab === "aiImpact" ? (
            <AiImpactAssessmentTable
              onOpenPlanManagement={onOpenPlanManagement}
            />
          ) : null}
        </Box>
      ) : null}
      {showManualTab ? (
        <Box
          sx={{
            display: tab === "manualImpact" ? "flex" : "none",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}
        >
          {tab === "manualImpact" ? (
            <ManualImpactAssessmentTable />
          ) : null}
        </Box>
      ) : null}
    </>
  );
}

export default function AssessmentCenterPage() {
  const navigate = useNavigate();
  const moduleDisplayName = useModuleDisplayName();
  const { has: canReadImpact, loaded: impactPrivilegeLoaded } =
    useHasPrivilegeStatus("gri.assessment.impact.read");
  const { has: canReadManual, loaded: manualPrivilegeLoaded } =
    useHasPrivilegeStatus("gri.assessment.manual.read");
  const { has: canProcessMapping, loaded: processMappingPrivilegeLoaded } =
    useHasPrivilegeStatus("gri.assessment.config.process_mapping");

  const historyPrivilegesLoaded =
    impactPrivilegeLoaded && manualPrivilegeLoaded;
  const showAccessDenied =
    historyPrivilegesLoaded && !canReadImpact && !canReadManual;
  const showAiTab = historyPrivilegesLoaded && canReadImpact;
  const showManualTab = historyPrivilegesLoaded && canReadManual;

  const [tab, setTab] = useState<AssessmentTabKey>("aiImpact");
  const [planManagementOpen, setPlanManagementOpen] = useState(false);
  const [processMappingOpen, setProcessMappingOpen] = useState(false);

  // When privileges load (or change), leave a tab the user cannot read.
  useEffect(() => {
    if (!historyPrivilegesLoaded) return;
    const tabAllowed =
      (tab === "aiImpact" && canReadImpact) ||
      (tab === "manualImpact" && canReadManual);
    if (tabAllowed) return;
    const next = pickDefaultAssessmentCenterTab(canReadImpact, canReadManual);
    if (next) setTab(next);
  }, [historyPrivilegesLoaded, canReadImpact, canReadManual, tab]);

  const handleCloseProcessMapping = useCallback(() => {
    setProcessMappingOpen(false);
  }, []);

  const handleTabChange = useCallback((next: AssessmentTabKey) => {
    setTab(next);
  }, []);

  // Avoid MUI Tabs warning when the active tab is hidden by privilege.
  const activeTab: AssessmentTabKey | false =
    (tab === "aiImpact" && showAiTab) ||
    (tab === "manualImpact" && showManualTab)
      ? tab
      : false;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box
        sx={{
          py: "16px",
          px: "24px",
          position: "relative",
          zIndex: 2,
          flexShrink: 0,
          bgcolor: "#fff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mb: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flex: 1,
              minWidth: 0,
            }}
          >
            <IconButton
              aria-label="Go back"
              onClick={() => navigate(-1)}
              sx={{
                width: 32,
                height: 32,
                p: 1,
                flexShrink: 0,
              }}
            >
              <Box
                component="img"
                src={arrowLeftIcon}
                alt=""
                sx={{
                  width: 16.25296401977539,
                  height: 16,
                  display: "block",
                  objectFit: "contain",
                }}
              />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Breadcrumbs
                separator="›"
                sx={{
                  mb: "4px",
                  "& .MuiBreadcrumbs-separator": { color: "#9ca3af", mx: 1 },
                }}
              >
                <Link
                  component="button"
                  variant="body1"
                  onClick={() => navigate("/")}
                  sx={{
                    color: "#2563eb",
                    textDecoration: "none",
                    cursor: "pointer",
                    fontSize: "0.875rem",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {moduleDisplayName}
                </Link>
                <Typography
                  sx={{
                    color: "#374151",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                  }}
                >
                  Assessment Center
                </Typography>
              </Breadcrumbs>
              <Typography
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontSize: "24px",
                  fontWeight: 700,
                  color: "#0f172a",
                  lineHeight: "32px",
                  letterSpacing: 0,
                  mb: "4px",
                }}
              >
                Assessment Center
              </Typography>
              <Typography
                sx={{
                  maxWidth: 760,
                  color: "#0D0D0D",
                  fontSize: "0.875rem",
                  lineHeight: 1.5,
                }}
              >
                A central place to view and track all executed impact
                assessments
              </Typography>
            </Box>
          </Box>
          {processMappingPrivilegeLoaded && canProcessMapping ? (
            <Button
              type="button"
              variant="outlined"
              size="small"
              onClick={() => setProcessMappingOpen(true)}
              startIcon={
                <Box
                  component="img"
                  src={iconProcessMapping}
                  alt=""
                  sx={{
                    width: 20,
                    height: 20,
                    display: "block",
                    objectFit: "contain",
                    pointerEvents: "none",
                  }}
                />
              }
              sx={{
                ...assessmentCenterPlansLikeButtonSx,
                alignSelf: "center",
                flexShrink: 0,
              }}
            >
              Process Mapping
            </Button>
          ) : null}
        </Box>
      </Box>

      <Box sx={pageSectionMainAreaSx}>
        <AssessmentCenterHistoryBody
          showAccessDenied={showAccessDenied}
          showAiTab={showAiTab}
          showManualTab={showManualTab}
          tab={tab}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          onOpenPlanManagement={() => setPlanManagementOpen(true)}
        />
      </Box>
      <PlanManagementDialog
        open={planManagementOpen}
        onClose={() => setPlanManagementOpen(false)}
      />
      <ProcessMappingSettingDialog
        open={processMappingOpen}
        onClose={handleCloseProcessMapping}
      />
    </Box>
  );
}
